import {
	ChangeDetectionStrategy,
	ChangeDetectorRef,
	Component,
	ElementRef,
	EventEmitter,
	HostBinding,
	Inject,
	OnDestroy,
	OnInit,
	Output,
	Renderer2,
	ViewChild,
	ViewEncapsulation,
} from "@angular/core";
import {
	MAT_LEGACY_DIALOG_DATA as MAT_DIALOG_DATA,
	MatLegacyDialog as MatDialog,
} from "@angular/material/legacy-dialog";
import { DialogLayoutComponent } from "@obg/mat.ui.core";
import { DeviceConfigService } from "@obg/ngx.global";
import { SequentialFlowStepperComponent } from "@obg/ngx.ui.common";
import { merge, Observable, Subscription } from "rxjs";
import { filter, tap } from "rxjs/operators";

import { NewLoginFlowStep } from "../../../login.model";
import { LoginFlowLoaderService } from "../../login-flow-loader.service";
import { LOGIN_FLOW_COMPONENTS_MAP_DESKTOP, LOGIN_FLOW_COMPONENTS_MAP_MOBILE } from "../../login-flow.const";
import { LoginFlowComponentStep } from "../../login-flow.model";
import { NewFlowDialogModel } from "../../new-flow-dialog.model";
import { NewFlowJurisdictionService } from "../../new-flow-jurisdiction.service";
import { StepperService } from "../../stepper.service";
import { VerificationData } from "../../verification.model";
import { VerificationService } from "../../verification.service";

@Component({
	selector: "obg-login-flow",
	templateUrl: "./login-flow.container.html",
	styleUrls: ["./login-flow.container.scss"],
	encapsulation: ViewEncapsulation.None,
	changeDetection: ChangeDetectionStrategy.OnPush,
	providers: [VerificationService],
})
export class LoginFlowContainer implements OnInit, OnDestroy {
	@HostBinding("class.obg-login-flow") hostCssClass = true;

	@HostBinding("style.min-height") get minHeight() {
		return this.data.minHeight;
	}

	@Output() close = new EventEmitter();

	@ViewChild(DialogLayoutComponent, { read: ElementRef }) dialogLayout!: ElementRef;
	@ViewChild(SequentialFlowStepperComponent) flowComponent!: SequentialFlowStepperComponent;

	verificationData$: Observable<VerificationData> | undefined;
	loading$: Observable<boolean> | undefined;
	newLoginFlowSteps: NewLoginFlowStep[] | undefined;
	hideFooter: boolean | undefined;
	flowStartIndex: number | undefined;
	flowComponentsMap: LoginFlowComponentStep[] | undefined;
	activeStep: NewLoginFlowStep | undefined;
	private data$$: Subscription | undefined;

	constructor(
		public loginFlowLoaderService: LoginFlowLoaderService,
		private verificationService: VerificationService,
		private changeDetector: ChangeDetectorRef,
		private matDialog: MatDialog,
		private stepperService: StepperService,
		private deviceConfigService: DeviceConfigService,
		private renderer: Renderer2,
		private newFlowJurisdictionService: NewFlowJurisdictionService,
		@Inject(MAT_DIALOG_DATA) private data: NewFlowDialogModel
	) {
		this.hideFooter = this.data.hideFooter;
		this.flowStartIndex = this.data.flowStartIndex;
		this.loading$ = this.loginFlowLoaderService.loading$;
		this.verificationData$ = this.verificationService.verificationData$();
	}

	ngOnInit() {
		const configSteps$ = this.newFlowJurisdictionService.getFlowStepsConfigOnJurisdictionChange$().pipe(
			tap((newLoginFlowSteps) => {
				this.newLoginFlowSteps = newLoginFlowSteps;
				const loginFlowComponentsMap = this.deviceConfigService.isDesktop()
					? LOGIN_FLOW_COMPONENTS_MAP_DESKTOP
					: LOGIN_FLOW_COMPONENTS_MAP_MOBILE;
				this.flowComponentsMap = loginFlowComponentsMap.filter((step) =>
					newLoginFlowSteps.some((configStep) => configStep.name === step.key)
				);
				this.changeDetector.detectChanges();
			})
		);

		this.verificationService.init();

		const navigateForward$ = this.stepperService.allowNavigateToNextStep$.pipe(
			tap((allowNextStep) => (allowNextStep ? this.flowComponent.goToNextStep() : this.matDialog.closeAll()))
		);

		const navigateByIndex$ = this.stepperService.navigateToStepByIndex$.pipe(
			filter((index) => !!index),
			tap((index) => {
				this.flowStartIndex = index;
				this.flowComponent.goToSelectedStep(this.flowStartIndex);
			})
		);

		this.data$$ = merge(configSteps$, navigateByIndex$, navigateForward$).subscribe(() =>
			this.changeDetector.markForCheck()
		);
	}

	get hideClose() {
		return this.activeStep?.data?.hideClose ?? this.data.hideClose;
	}

	setActiveStep(step: NewLoginFlowStep) {
		this.activeStep = step;
		this.loginFlowLoaderService.toggleLoading(false);
	}

	onHeightChangeAnimationStart() {
		this.renderer.setStyle(this.dialogLayout.nativeElement.children[0], "overflow", "hidden");
	}

	onHeightChangeAnimationDone() {
		this.renderer.setStyle(this.dialogLayout.nativeElement.children[0], "overflow", "auto");
	}

	ngOnDestroy() {
		this.data$$?.unsubscribe();
	}
}
